import classNames from 'classnames';
import pluralize from 'pluralize';
import { memo, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ComplianceProgressModal from '@/modules/admin-dashboard/components/modals/ComplianceProgressModal.tsx';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { ModalProviderContext } from 'ui';
import { RegisterExtendResponse, RegisterResult, TaskListMode } from 'compliance-api';

interface RegisterProgressBarProps {
  register: RegisterExtendResponse;
  fromDate?: string;
  toDate?: string;
}

const ProgressStatusPart = ({
  name,
  color,
  percent,
  quantity,
  navigation,
}: {
  percent: number;
  quantity: number;
  name: string;
  color: string;
  navigation: {
    register: RegisterExtendResponse;
    compliance?: RegisterResult;
    fromDate?: string;
    toDate?: string;
  };
}) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);

  const { register, compliance, fromDate, toDate } = navigation;

  const handleClick = useCallback(() => {
    if (compliance) {
      openModal(ComplianceProgressModal, {
        initResult: compliance,
        initRegister: { label: register.name, value: register.id },
        fromDate,
        toDate,
      });
    } else {
      navigate(
        {
          pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Todo),
          search: params.toString(),
        },
        {
          state: {
            from_date: fromDate,
            to_date: toDate,
            initFilters: {
              [TaskListMode.Todo]: { register: { label: register.name, value: register.id } },
            },
          },
        },
      );
    }
  }, [compliance, fromDate, navigate, openModal, params, register.id, register.name, toDate]);

  if (!percent) return null;
  return (
    <div
      data-tooltip-id={'tasks_activity_tooltip'}
      data-tooltip-html={`${quantity} ${pluralize(
        compliance ? 'answer' : 'task',
        quantity,
      )} in "${name}" status`}
      className={`cursor-pointer hover:h-2 hover:-mt-0.5 h-full first:rounded-l last:rounded-r`}
      style={{ width: `${percent}%`, backgroundColor: color }}
      onClick={handleClick}
    />
  );
};

const RegisterProgressBar = ({ register, fromDate, toDate }: RegisterProgressBarProps) => {
  const {
    compliant_task_results_quantity = 0,
    non_applicable_task_results_quantity = 0,
    not_compliant_task_results_quantity = 0,
    in_progress_task_results_quantity = 0,
    not_completed_task_results_quantity = 0,
  } = register;

  const totalQuantity =
    compliant_task_results_quantity +
    non_applicable_task_results_quantity +
    not_compliant_task_results_quantity +
    in_progress_task_results_quantity;

  const compliantPercent = totalQuantity && (compliant_task_results_quantity / totalQuantity) * 100;
  const notApplicablePercent =
    totalQuantity && (non_applicable_task_results_quantity / totalQuantity) * 100;
  const notCompliantPercent =
    totalQuantity && (not_compliant_task_results_quantity / totalQuantity) * 100;
  const inProgressPercent =
    totalQuantity && (in_progress_task_results_quantity / totalQuantity) * 100;
  const notCompletedPercent =
    totalQuantity && (not_completed_task_results_quantity / totalQuantity) * 100;

  return (
    <div
      className={classNames('h-1 w-full rounded flex gap-0.5', { 'bg-gray-500': !totalQuantity })}
    >
      <ProgressStatusPart
        percent={compliantPercent}
        quantity={compliant_task_results_quantity}
        name="Compliant"
        color="#16A34A"
        navigation={{ register, compliance: RegisterResult.Compliant, fromDate, toDate }}
      />
      <ProgressStatusPart
        percent={notCompliantPercent}
        quantity={not_compliant_task_results_quantity}
        name="Not compliant"
        color="#DC2626"
        navigation={{ register, compliance: RegisterResult.NotCompliant, fromDate, toDate }}
      />
      <ProgressStatusPart
        percent={notApplicablePercent}
        quantity={non_applicable_task_results_quantity}
        name="N/A"
        color="#2626DC"
        navigation={{ register, compliance: RegisterResult.NonApplicable, fromDate, toDate }}
      />
      <ProgressStatusPart
        percent={inProgressPercent}
        quantity={in_progress_task_results_quantity}
        name="In progress"
        color="#E07211"
        navigation={{ register, compliance: RegisterResult.InProgress, fromDate, toDate }}
      />
      <ProgressStatusPart
        percent={notCompletedPercent}
        quantity={not_completed_task_results_quantity}
        name="Not completed"
        color="#CCCCCC"
        navigation={{ register, fromDate, toDate }}
      />
      <ReactTooltip id="tasks_activity_tooltip" />
    </div>
  );
};

export default memo(RegisterProgressBar);
